import './Asset.scss'

import { VideoPlayer, VideoPlayerProps } from '@bob/library'
import URLSearchParams from '@ungap/url-search-params'
import { Video23, Video23Type } from '@websrc/App/Components/Asset/Video23/Video23'
import { mapAsset } from '@websrc/DataMappers/mapAsset'
import React, { memo } from 'react'

import { Animation } from './Animation/Animation'
import { IImageProps, Image } from './Image/Image'

export type VideoCSType = {
  type: 'cs_video'
  sources: VideoPlayerProps['options']['sources']
  tracks?: VideoPlayerProps['options']['tracks']
  poster?: string
}

export type AnimationType = {
  type: 'animation'
  loop?: boolean
  forcePause?: boolean
  defaultTime?: number
  className?: string
} & ReturnType<typeof mapAsset>

export type VideoType = Video23Type | VideoCSType | AnimationType | undefined

type ParamsProps = {
  format?: string
  fit?: 'bounds' | 'crop'
  height?: string | number
  width?: string | number
  quality?: number
  disable?: 'upscale' | ''
}

export type AssetProps = Omit<ParamsProps, 'format' | 'locale'> & {
  thumbnail?: {
    url?: string | null
    title?: string
  }
  video?: VideoType
  className?: string
  style?: React.CSSProperties
  lazyLoading?: boolean
  defaultTime?: number
  sizes?: string
  srcSetWidths?: number[]
  dataTestId?: string
  objectFit?: IImageProps['objectFit']
  forcePauseVideo?: boolean
  resetOnPause?: boolean
  loopAnimation?: boolean
  fillVideo?: boolean
  altText?: string
  animationClassName?: string
}

function calcCSImageUrl(url: string, params: ParamsProps) {
  const splitUrl = url.split('?')
  const searchParams = new URLSearchParams(splitUrl[1])
  const baseUrl = splitUrl[0]

  searchParams.set('auto', 'webp')
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value.toString())
    }
  })

  if (params.format?.toLowerCase() === 'svg') {
    return baseUrl
  }

  return `${baseUrl}?${searchParams.toString()}`
}

function calcSrcSet(url: string, imageSizes: number[], params: ParamsProps) {
  const ratio =
    typeof params.height === 'number' && typeof params.width === 'number'
      ? parseFloat((params.height / params.width).toFixed(2))
      : undefined

  return imageSizes
    .map(
      (size) =>
        `${calcCSImageUrl(url, {
          ...params,
          width: size + 200,
          height: ratio && ratio * size,
        })} ${size}w`
    )
    .join(', ')
}

const assetPropsAreEqual = (prev: AssetProps, next: AssetProps) => {
  const keys = Object.keys(prev) as (keyof AssetProps)[]
  return !keys.some((key) => {
    if (key === 'srcSetWidths') return false
    return prev[key] !== next[key]
  })
}

export const Asset = memo((props: AssetProps) => {
  const {
    className,
    style,
    thumbnail,
    video,
    width = 1800,
    height,
    fit = 'bounds',
    lazyLoading,
    sizes,
    srcSetWidths,
    disable = '',
    dataTestId,
    objectFit,
    forcePauseVideo,
    animationClassName,
    altText,
    fillVideo,
    loopAnimation,
    resetOnPause,
    defaultTime,
  } = props

  let format = 'jpeg'
  if (thumbnail?.url) {
    const matches = /\.([a-zA-Z]+)$/.exec(thumbnail.url)
    if (matches) format = matches[1]
  }

  const params: ParamsProps = {
    format,
    width,
    height,
    quality: 90,
    fit,
    disable,
  }

  let content: React.ReactNode = null

  switch (video?.type) {
    case 'cs_video':
      content = (
        <VideoPlayer
          options={{
            fluid: true,
            poster: thumbnail?.url ? calcCSImageUrl(thumbnail.url, { ...params, width: 900 }) : '',
            tracks: video.tracks,
            sources: video.sources,
          }}
        />
      )
      break

    case '23_video':
      content = (
        <Video23 {...video} forcePause={forcePauseVideo} fillVideo={fillVideo}>
          {thumbnail?.url && (
            <Image
              alt={thumbnail.title}
              lazy={lazyLoading}
              sizes={sizes}
              src={calcCSImageUrl(thumbnail.url, params)}
              srcSet={srcSetWidths && calcSrcSet(thumbnail.url, srcSetWidths, params)}
            />
          )}
        </Video23>
      )
      break

    case 'animation':
      content = (
        <Animation
          {...video}
          className={animationClassName}
          defaultTime={defaultTime}
          forcePause={forcePauseVideo}
          loop={loopAnimation}
          resetOnPause={resetOnPause}
          thumbnailAlt={altText ?? thumbnail?.title}
          thumbnailUrl={thumbnail?.url ? calcCSImageUrl(thumbnail.url, params) : undefined}
        />
      )
      break

    default:
      if (thumbnail?.url) {
        content = (
          <Image
            alt={altText ?? thumbnail.title}
            lazy={lazyLoading}
            objectFit={objectFit}
            sizes={sizes}
            src={calcCSImageUrl(thumbnail.url, params)}
            srcSet={srcSetWidths && calcSrcSet(thumbnail.url, srcSetWidths, params)}
            style={style}
          />
        )
      }
  }

  const hasDimensions = typeof height === 'number' && typeof width === 'number'

  return animationClassName ? (
    <>{content}</>
  ) : (
    <div
      className={`asset${disable === 'upscale' ? ' asset--disable-upscale' : ''}${
        hasDimensions ? ' has-dimensions' : ''
      } ${className ?? ''}`}
      data-testid={dataTestId}
    >
      {content}
    </div>
  )
}, assetPropsAreEqual)
